import { useI18N } from '@susu/i18n/client'
import {
  Button,
  Icon,
  Loader,
  SelectField,
  type SelectOption,
} from '@susu/ui/components'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { ScreenLayout } from '../../components/ScreenLayout'
import Avo, { ErrorType, SelectedCountry } from '../../../Avo/Avo'
import { flowProperties } from '../../lib/analytics'
import { resolveAndValidateCountryCode } from '../../lib/locale'
import { useBookingStore } from '../../store'
import { getCountriesWithStores } from './api/stores-api'
import type {
  CountryResponse,
  HoursStatus,
  StoreResponse,
} from './StoreLocator.types'
import { formatStoreAddress, getStoreHoursInfo } from './StoreLocator.utils'

const HOURS_STATUS_CLASS: Record<HoursStatus, string> = {
  open: 'text-text-success',
  'opens-later': 'text-text-secondary',
  closed: 'text-text-error',
}

export interface StoreLocatorProps {
  onClose?: () => void
}

export const StoreLocator = ({ onClose }: StoreLocatorProps) => {
  const { t } = useI18N()
  const {
    setState,
    navigate,
    language,
    location,
    selectedCountry: storedCountry,
  } = useBookingStore()
  const [selectedCountry, setSelectedCountry] = useState<string>(
    storedCountry ?? '',
  )
  const initializedRef = useRef(false)

  // Once per mount — this screen has one step, so nothing to gate on load state.
  useEffect(() => {
    Avo.bookingToolStepViewed(flowProperties())
  }, [])

  const {
    data: countries,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['countries-with-stores'],
    queryFn: getCountriesWithStores,
    staleTime: 5 * 60 * 1000,
  })

  // Fire once per failure, not on every re-render while the query stays errored.
  const erroredRef = useRef(false)
  useEffect(() => {
    if (!isError) {
      erroredRef.current = false
      return
    }
    if (erroredRef.current) return
    erroredRef.current = true
    Avo.bookingToolError({
      ...flowProperties(),
      errorType: ErrorType.STORES_FAILED,
    })
  }, [isError])

  // Auto-select the country derived from the current locale once the list loads.
  // If the user already picked a country (storedCountry), skip re-initialization
  // so back-navigation preserves their selection.
  // Resolution order: stored selection → URL ?countryCode param → URL locale path
  // → language locale string → store location → Netherlands as the final fallback.
  useEffect(() => {
    if (!countries || initializedRef.current) return
    initializedRef.current = true

    if (storedCountry) return

    const initial = resolveAndValidateCountryCode(
      countries.map((c) => c.countryCode),
      language,
      location,
    )
    setSelectedCountry(initial)
    setState({ selectedCountry: initial })
  }, [countries, language, location, storedCountry, setState])

  const filteredStores: StoreResponse[] =
    countries?.find((c) => c.countryCode === selectedCountry)?.stores ?? []

  const countryOptions: SelectOption[] =
    countries?.map((c: CountryResponse) => ({
      value: c.countryCode,
      label: c.country,
    })) ?? []

  const handleCountryChange = (code: string) => {
    setSelectedCountry(code)
    setState({ selectedCountry: code })
    // `country` stays the host/locale country; the pick goes as
    // `selectedCountry`, lowercased — the API's `countryCode` is uppercase.
    // A country the plan does not list is not sent.
    const selected = Object.values(SelectedCountry).find(
      (value) => value === code.toLowerCase(),
    )
    if (selected)
      Avo.bookingToolCountrySelected({
        ...flowProperties(),
        selectedCountry: selected,
      })
  }

  const handleStoreSelect = (store: StoreResponse) => {
    setState({
      storeId: store.storeIdC,
      storeName: store.name,
      locationCode: store.locationCode,
      wwLocationId: store.waitwhileLocationIdC,
    })
    // Fire before navigate(): both read the still-current `store-locator`
    // screen off the store — firing after would report step 2 (employees-list).
    Avo.bookingToolStoreSelected(flowProperties())
    Avo.bookingToolStepCompleted(flowProperties())
    navigate('employees-list')
  }

  return (
    <ScreenLayout
      title={t('storeLocator.title')}
      titleTestId="store-locator-heading"
      onClose={onClose}
      hasDropdown
    >
      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <Loader className="h-auto w-auto" ariaLabel={t('common.loading')} />
        </div>
      )}

      {isError && !isLoading && (
        <div className="flex-1 flex flex-col items-center justify-center gap-24 px-32 text-center">
          <p
            className="text-base leading-[24px] text-text"
            data-testid="store-locator-error"
          >
            {t('common.error')}
          </p>
          <Button
            variant="outlined"
            size="m"
            onClick={() => refetch()}
            data-testid="store-locator-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          {/* Country selector */}
          <div className="pt-24 pb-20" data-testid="store-locator-dropdown">
            <SelectField
              id="country-select"
              name="country"
              label={t('storeLocator.countryLabel')}
              value={selectedCountry}
              options={countryOptions}
              onChange={handleCountryChange}
              placeholder={t('storeLocator.selectCountry')}
              data-testid="store-locator-select"
            />
          </div>

          {/* Store list */}
          <div className="flex-1 overflow-y-auto">
            {selectedCountry && filteredStores.length === 0 && (
              <div className="px-16 py-24 text-text-secondary text-base leading-base text-center">
                {t('storeLocator.noStores')}
              </div>
            )}

            {filteredStores.map((store) => {
              const { line1, line2 } = formatStoreAddress(store)
              const hoursInfo = getStoreHoursInfo(store)
              const hoursLabel = hoursInfo
                ? t(
                    hoursInfo.key,
                    hoursInfo.time ? { time: hoursInfo.time } : undefined,
                  )
                : null

              return (
                <button
                  key={store.id}
                  type="button"
                  onClick={() => handleStoreSelect(store)}
                  className="w-full flex items-center px-16 py-20 border-t border-border text-left hover:bg-surface-hover transition-[background-color] duration-150 group"
                  data-testid="store-locator-row"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-lg leading-lg font-medium text-text truncate">
                      {store.name}
                    </p>
                    {line1 && (
                      <p
                        className="mt-2 text-base leading-base font-light text-text-secondary truncate"
                        data-testid={`store-locator-row-${store.name}`}
                      >
                        {line1}
                      </p>
                    )}
                    {line2 && (
                      <p className="text-base leading-base font-light text-text-secondary truncate">
                        {line2}
                      </p>
                    )}
                    {hoursInfo && hoursLabel && (
                      <p
                        className={`mt-4 text-sm leading-sm font-light ${HOURS_STATUS_CLASS[hoursInfo.status]}`}
                      >
                        {hoursLabel}
                      </p>
                    )}
                  </div>
                  <span className="ml-12 flex-shrink-0 text-icon-secondary group-hover:text-icon transition-[color] duration-150">
                    <Icon name="chevron-right" size={20} />
                  </span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </ScreenLayout>
  )
}

export default StoreLocator
